import PostCard from "@/components/posts/PostCard";
import { ThemeColors, useColors } from "@/constants/theme";
import { useAppStore } from "@/stores/useStore";
import { Visibility } from "@/types";
import { Link } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { FlatList, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

type TimelineFilter = "all" | Visibility | "mine";

const TIMELINE_FILTERS: { id: TimelineFilter; label: string }[] = [
  { id: "all", label: "すべて" },
  { id: "class", label: "クラス" },
  { id: "school", label: "学校" },
  { id: "public", label: "公開" },
  { id: "mine", label: "自分" },
];

export default function TimelineScreen() {
  const { posts, currentUser, hiddenPostIds, loadRemoteData } = useAppStore();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const [selectedFilter, setSelectedFilter] = useState<TimelineFilter>("all");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadRemoteData().catch((error) => {
      console.warn("Failed to refresh timeline", error);
    });
  }, [loadRemoteData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadRemoteData();
    } catch (error) {
      console.warn("Failed to refresh timeline", error);
    } finally {
      setRefreshing(false);
    }
  };

  const visiblePosts = useMemo(() => {
    return posts
      .filter((post) => post.status !== "deleted")
      .filter((post) => !hiddenPostIds.includes(post.id))
      .filter((post) => {
        if (currentUser) {
          return post.visibility !== "private" || post.user_id === currentUser.id;
        }

        return post.visibility === "public";
      })
      .filter((post) => {
        if (selectedFilter === "all") return true;
        if (selectedFilter === "mine") return post.user_id === currentUser?.id;
        return post.visibility === selectedFilter;
      })
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [currentUser, hiddenPostIds, posts, selectedFilter]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="headlineSmall" style={styles.title}>
          🏠 ホーム
        </Text>
        <Text variant="bodySmall" style={styles.subtitle}>
          みんなの学びの発見
        </Text>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.filterContainer}
        contentContainerStyle={styles.filterContent}
      >
        {TIMELINE_FILTERS.map((filter) => (
          <Pressable
            key={filter.id}
            accessibilityRole="button"
            onPress={() => setSelectedFilter(filter.id)}
            style={[
              styles.filterChip,
              selectedFilter === filter.id && styles.filterChipActive,
            ]}
          >
            <Text
              style={[
                styles.filterText,
                selectedFilter === filter.id && styles.filterTextActive,
              ]}
            >
              {filter.label}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      <FlatList
        data={visiblePosts}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => <PostCard post={item} />}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>📝 まだ発見がありません。</Text>
            <Text style={styles.emptySubtext}>
              学問レンズを使って、日常の発見を記録しましょう！
            </Text>
            <Link href="/(tabs)/create" asChild>
              <Pressable accessibilityRole="button" style={styles.emptyCta}>
                <Text style={styles.emptyCtaText}>✨ 最初の発見を投稿</Text>
              </Pressable>
            </Link>
          </View>
        }
      />
    </View>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.bg,
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: c.surface,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    width: "100%",
    maxWidth: 980,
    alignSelf: "center",
  },
  title: {
    fontWeight: "700",
    marginBottom: 2,
    color: c.textPrimary,
  },
  subtitle: {
    color: c.textSecondary,
  },
  filterContainer: {
    backgroundColor: c.surface,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
    flexGrow: 0,
  },
  filterContent: {
    width: "100%",
    maxWidth: 980,
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
  },
  filterChip: {
    minWidth: 72,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: 18,
    backgroundColor: c.surface,
  },
  filterChipActive: {
    backgroundColor: c.primarySoft,
    borderColor: c.primary,
  },
  filterText: {
    color: c.textSecondary,
    fontSize: 13,
    fontWeight: "700",
  },
  filterTextActive: {
    color: c.primaryDark,
  },
  listContent: {
    width: "100%",
    maxWidth: 980,
    alignSelf: "center",
    paddingVertical: 12,
    paddingBottom: 28,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: 60,
  },
  emptyText: {
    textAlign: "center",
    color: c.textSecondary,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
  },
  emptySubtext: {
    textAlign: "center",
    color: c.textMuted,
    fontSize: 13,
  },
  emptyCta: {
    marginTop: 20,
    minHeight: 46,
    borderRadius: 23,
    backgroundColor: c.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  emptyCtaText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 14,
  },
});
